/**
 * Position Domain Schemas
 *
 * Only the response shapes actually consumed by the analytics/heatmap features
 * remain here. DEV2-1271 removed the real-time WebSocket/edge-device position
 * scaffolding (positionSchema, positionUpdateSchema, positionBatchSchema,
 * positionFilterSchema, enrichedPositionSchema, coordinate/accelerometer
 * schemas, and their helper functions) — that ingestion path was never wired
 * up; live position data flows through `HistoricalPosition`
 * (`src/services/position.service.ts`) and the WebSocket domain-events types
 * instead.
 */

import { z } from 'zod';

/**
 * Factory-wide analytics KPIs returned by GET /api/factories/:factoryId/analytics.
 *
 * Mirrors the backend AnalyticsKpisResponse contract (DEV2-444).
 *
 * Units:
 * - totalDistance: meters
 * - avgSpeed: km/h (backend already converts; do NOT multiply by 3.6)
 * - idleTime / forkTime / workingTime / movementTime: seconds
 * - assetPerformance: percent (0-100)
 *
 * workingTime = movementTime + forkTime
 */
export const positionAnalyticsSchema = z.object({
  factoryId: z.string(),
  totalDistance: z.number(),
  avgSpeed: z.number(),
  activeAssets: z.number(),
  idleTime: z.number(),
  forkTime: z.number(),
  workingTime: z.number(),
  movementTime: z.number(),
  assetPerformance: z.number(),
  stale: z.boolean().optional(),
  cachedAt: z.string().optional(),
});

export type PositionAnalytics = z.infer<typeof positionAnalyticsSchema>;

// Position heatmap data schema
export const positionHeatmapSchema = z.object({
  x: z.number(),
  y: z.number(),
  intensity: z.number().min(0).max(1), // Normalized intensity 0-1
  count: z.number(), // Number of position points in this grid cell
});

export type PositionHeatmap = z.infer<typeof positionHeatmapSchema>;

export const positionHeatmapResponseSchema = z.object({
  factoryId: z.string(),
  timeRange: z.object({
    start: z.date(),
    end: z.date(),
  }),
  gridSize: z.number(), // Grid cell size in meters
  heatmapData: z.array(positionHeatmapSchema),
});

export type PositionHeatmapResponse = z.infer<typeof positionHeatmapResponseSchema>;
